import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useLayout } from './LayoutContext';

interface NavItem {
  to?: string;
  icon: string; // Bootstrap icon class
  label: string;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  {
    to: '/',
    icon: 'bi-speedometer2',
    label: 'Dashboard',
  },
  {
    icon: 'bi-activity',
    label: 'Monitoring',
    children: [
      { to: '/on-chain', icon: 'bi-shield-check', label: 'On-Chain' },
      { to: '/off-chain', icon: 'bi-cloud-check', label: 'Off-Chain' },
    ],
  },
  {
    icon: 'bi-currency-dollar',
    label: 'Financial',
    children: [
      { to: '/revenue', icon: 'bi-cash-stack', label: 'Company Revenue' },
      { to: '/treasury', icon: 'bi-safe2', label: 'Treasury' },
    ],
  },
  {
    to: '/logs',
    icon: 'bi-file-earmark-text',
    label: 'Logs',
  },
];

interface SidebarMenuItemProps {
  item: NavItem;
  level?: number;
}

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({ item, level = 0 }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(
    item.children?.some((child) => child.to === location.pathname) || false
  );

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.to === location.pathname;
  const isParentActive = hasChildren && item.children!.some((child) => child.to === location.pathname);

  if (hasChildren) {
    return (
      <li className="nav-item">
        <a
          className={`nav-link ${isParentActive ? 'active' : ''}`}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(!isOpen);
          }}
          aria-expanded={isOpen}
        >
          <i className={`nav-icon ${item.icon}`}></i>
          <p>
            {item.label}
            <i className={`nav-arrow bi bi-chevron-${isOpen ? 'down' : 'right'}`}></i>
          </p>
        </a>
        <ul className={`nav nav-treeview ${isOpen ? 'menu-open' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}>
          {item.children!.map((child) => (
            <SidebarMenuItem key={child.to || child.label} item={child} level={level + 1} />
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li className="nav-item">
      <NavLink
        to={item.to!}
        className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
      >
        <i className={`nav-icon ${item.icon} ${level > 0 ? '' : ''}`}></i>
        <p>{item.label}</p>
      </NavLink>
    </li>
  );
};

export const Sidebar: React.FC = () => {
  const { sidebarOpen } = useLayout();

  return (
    <aside
      className="app-sidebar bg-body-tertiary shadow"
      style={{
        width: '250px',
        position: 'fixed',
        top: '56px',
        bottom: 0,
        left: sidebarOpen ? 0 : '-250px',
        zIndex: 1000,
        overflow: 'auto',
        transition: 'left 0.3s ease',
      }}
    >
      {/* Sidebar Navigation */}
      <div className="sidebar-wrapper">
        <nav className="mt-3">
          <ul className="nav nav-pills nav-sidebar flex-column px-2" role="menu">
            {navItems.map((item) => (
              <SidebarMenuItem key={item.to || item.label} item={item} />
            ))}
          </ul>
        </nav>
      </div>
    </aside>
  );
};
